import { defaultState, STORAGE_KEY } from "./defaultState";
import { localDateKey } from "./logic";
import { defaultMetricUnitsForLanguage, normalizeMetricUnits, settingsWithMetricUnits } from "./metric-units";
import { settingsWithWeeklyGoalCategories } from "./weekly-goal-categories";
import type { AppState, DailyItem, HabitRepeat, RagConfidence, RagIngestionStatus, RagSource, RagSourceType, StreakFreeDateRange, WeeklyGoal } from "./types";

const LEGACY_STORAGE_KEYS = ["disciplio-dark-right-nav-v2"];

const repeatCadences = new Set(["none", "daily", "customDays", "weekly", "monthly"]);
const ragSourceTypes = new Set(["book", "blog", "research"]);
const ragConfidences = new Set(["low", "medium", "high"]);
const ragIngestionStatuses = new Set(["ready", "chunked", "embedded", "error"]);
const datePattern = /^\d{4}-\d{2}-\d{2}$/;

function normalizeRepeat(value: unknown, fallback?: HabitRepeat): HabitRepeat {
  if (!value || typeof value !== "object") return fallback ?? { cadence: "none", interval: 1 };
  const record = value as Partial<HabitRepeat>;
  const cadence = typeof record.cadence === "string" && repeatCadences.has(record.cadence)
    ? record.cadence
    : (fallback?.cadence ?? "none");
  const interval = Number(record.interval);
  const excludedWeekdays = Array.isArray(record.excludedWeekdays)
    ? record.excludedWeekdays
        .map((day) => Number(day))
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
    : (fallback?.excludedWeekdays ?? []);
  const excludedDates = Array.isArray(record.excludedDates)
    ? record.excludedDates.filter((date) => typeof date === "string" && datePattern.test(date))
    : (fallback?.excludedDates ?? []);
  return {
    cadence: cadence as HabitRepeat["cadence"],
    interval: Number.isFinite(interval) && interval > 0 ? interval : (fallback?.interval ?? 1),
    excludedWeekdays,
    excludedDates,
  };
}

function uniqueStrings(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return Array.from(new Set(values.map((value) => (typeof value === "string" ? value.trim() : "")).filter(Boolean)));
}

function normalizeFreeDateRanges(value: unknown): StreakFreeDateRange[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index): StreakFreeDateRange | null => {
      if (!entry || typeof entry !== "object") return null;
      const record = entry as Partial<StreakFreeDateRange>;
      const start = typeof record.start === "string" && datePattern.test(record.start) ? record.start : "";
      const end = typeof record.end === "string" && datePattern.test(record.end) ? record.end : start;
      if (!start || !end) return null;
      return {
        id: typeof record.id === "string" && record.id ? record.id : `free-${index}-${start}`,
        start: start <= end ? start : end,
        end: start <= end ? end : start,
      };
    })
    .filter((entry): entry is StreakFreeDateRange => Boolean(entry));
}

function normalizeLanguage(value: unknown): string {
  if (typeof value !== "string") return defaultState.settings.language;
  const language = value.trim().toLowerCase();
  if (language === "pl" || language === "uk") return language;
  return defaultState.settings.language;
}

function normalizeWeeklyGoal(goal: WeeklyGoal): WeeklyGoal {
  const category = typeof goal.category === "string" ? goal.category.trim() : "";
  return {
    ...goal,
    category: category || undefined,
    completed: Boolean(goal.completed),
  };
}

function normalizeProfileCompletionMap(value: unknown): Record<string, boolean> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, boolean>>((acc, [key, entry]) => {
    if (typeof key === "string" && key.trim()) {
      acc[key] = Boolean(entry);
    }
    return acc;
  }, {});
}

function normalizeRagSources(value: unknown): RagSource[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index): RagSource | null => {
      if (!entry || typeof entry !== "object") return null;
      const record = entry as Partial<RagSource>;
      const now = new Date().toISOString();
      const topic = typeof record.topic === "string" ? record.topic.trim() : "";
      const principle = typeof record.principle === "string" ? record.principle.trim() : "";
      const content = typeof record.content === "string" ? record.content.trim() : "";
      const actionableAdvice = typeof record.actionableAdvice === "string" ? record.actionableAdvice.trim() : "";
      const sourceUrl = typeof record.sourceUrl === "string" ? record.sourceUrl.trim() : "";
      if (!topic && !principle && !content && !actionableAdvice && !sourceUrl) return null;
      const sourceType = typeof record.sourceType === "string" && ragSourceTypes.has(record.sourceType)
        ? record.sourceType as RagSourceType
        : "book";
      const confidence = typeof record.confidence === "string" && ragConfidences.has(record.confidence)
        ? record.confidence as RagConfidence
        : "medium";
      const ingestionStatus = typeof record.ingestionStatus === "string" && ragIngestionStatuses.has(record.ingestionStatus)
        ? record.ingestionStatus as RagIngestionStatus
        : "ready";
      const source: RagSource = {
        id: typeof record.id === "string" && record.id ? record.id : `rag-${index}-${Date.now()}`,
        topic,
        principle,
        content,
        actionableAdvice,
        sourceType,
        confidence,
        sourceUrl,
        ingestionStatus,
        createdAt: typeof record.createdAt === "string" ? record.createdAt : now,
        updatedAt: typeof record.updatedAt === "string" ? record.updatedAt : now,
      };
      if (typeof record.errorMessage === "string") {
        source.errorMessage = record.errorMessage;
      }
      return source;
    })
    .filter((entry): entry is RagSource => Boolean(entry));
}

function normalizeDailyItem(item: DailyItem): DailyItem {
  const fallback: HabitRepeat = item.type === "habit" ? { cadence: "daily", interval: 1 } : { cadence: "none", interval: 1 };
  return {
    ...item,
    repeat: normalizeRepeat(item.repeat, fallback),
  };
}

function normalizeDailyItemsByDate(value: unknown): Record<string, DailyItem[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, DailyItem[]>>((acc, [key, items]) => {
    if (!datePattern.test(key) || !Array.isArray(items)) return acc;
    acc[key] = items.map((item) => normalizeDailyItem(item as DailyItem));
    return acc;
  }, {});
}

export function loadState(allowOptionalStorage = true): AppState {
  if (!allowOptionalStorage) return structuredClone(defaultState);
  LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return structuredClone(defaultState);
  try {
    const parsed = JSON.parse(raw) as Partial<AppState>;
    const persistedState = Object.fromEntries(
      Object.entries(parsed).filter(([key]) => key in defaultState),
    ) as Partial<AppState>;
    const profile = {
      ...structuredClone(defaultState.profile),
      ...(parsed.profile ?? {}),
      yearGoalLinks:
        parsed.profile?.yearGoalLinks && typeof parsed.profile.yearGoalLinks === "object"
          ? parsed.profile.yearGoalLinks
          : {},
      yearGoalCompleted: normalizeProfileCompletionMap(parsed.profile?.yearGoalCompleted),
      globalGoalCompleted: normalizeProfileCompletionMap(parsed.profile?.globalGoalCompleted),
      yearGoalMetrics: normalizeProfileMetricMap(parsed.profile?.yearGoalMetrics),
      globalGoalMetrics: normalizeProfileMetricMap(parsed.profile?.globalGoalMetrics),
    };
    const settings = settingsWithWeeklyGoalCategories(settingsWithMetricUnits({
      ...structuredClone(defaultState.settings),
      ...(parsed.settings ?? {}),
      timezone: normalizeTimezone(parsed.settings?.timezone),
      language: normalizeLanguage(parsed.settings?.language),
      dayStartTime: normalizeDayStartTime(parsed.settings?.dayStartTime),
      defaultMetricUnits: defaultMetricUnitsForLanguage(normalizeLanguage(parsed.settings?.language)),
      customMetricUnits: normalizeMetricUnits(parsed.settings?.customMetricUnits),
      streakFreeWeekdays: Array.isArray(parsed.settings?.streakFreeWeekdays)
        ? parsed.settings.streakFreeWeekdays
            .map((day) => Number(day))
            .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
        : [],
      streakFreeDateRanges: normalizeFreeDateRanges(parsed.settings?.streakFreeDateRanges),
      customWeeklyGoalCategories: uniqueStrings(parsed.settings?.customWeeklyGoalCategories),
      customWeeklyGoalCategoryLimit:
        typeof parsed.settings?.customWeeklyGoalCategoryLimit === "number"
          ? parsed.settings.customWeeklyGoalCategoryLimit
          : defaultState.settings.customWeeklyGoalCategoryLimit,
    }));
    const weeklyGoals = Array.isArray(parsed.weeklyGoals)
      ? parsed.weeklyGoals.map((goal) => normalizeWeeklyGoal(goal as WeeklyGoal))
      : structuredClone(defaultState.weeklyGoals);
    const categorySeed = [
      ...defaultState.weeklyGoalCategories,
      ...weeklyGoals.map((goal) => goal.category ?? ""),
      ...uniqueStrings(parsed.weeklyGoalCategories),
    ];
    const migratedDailyItems = Array.isArray(parsed.dailyItems)
      ? parsed.dailyItems.map((item) => normalizeDailyItem(item as DailyItem))
      : structuredClone(defaultState.dailyItems);
    const normalizedByDate = normalizeDailyItemsByDate(parsed.dailyItemsByDate);
    if (Object.keys(normalizedByDate).length === 0 && migratedDailyItems.length > 0) {
      const todayKey = localDateKey(new Date());
      normalizedByDate[todayKey] = migratedDailyItems;
    }
    return {
      ...structuredClone(defaultState),
      ...persistedState,
      profile,
      settings,
      weeklyGoalCategories: uniqueStrings(categorySeed),
      weeklyGoals,
      monthlyGoals: Array.isArray(parsed.monthlyGoals)
        ? parsed.monthlyGoals.map((goal) => ({ ...goal, completed: Boolean((goal as { completed?: boolean }).completed) }))
        : structuredClone(defaultState.monthlyGoals),
      dailyItems: migratedDailyItems,
      dailyItemsByDate: normalizedByDate,
      ragSources: normalizeRagSources(parsed.ragSources),
      dismissedSuggestionIds: Array.isArray(parsed.dismissedSuggestionIds) ? parsed.dismissedSuggestionIds : [],
      updatedAt:
        typeof parsed.updatedAt === "string" && parsed.updatedAt.length > 0
          ? parsed.updatedAt
          : defaultState.updatedAt,
      schemaVersion: defaultState.schemaVersion,
    };
  } catch {
    return structuredClone(defaultState);
  }
}

function normalizeProfileMetricMap(value: unknown): Record<string, { target: number; unit: string; progress: number }> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, { target: number; unit: string; progress: number }>>((metrics, [title, rawMetric]) => {
    if (!rawMetric || typeof rawMetric !== "object" || Array.isArray(rawMetric)) return metrics;
    const metric = rawMetric as Record<string, unknown>;
    const target = Number(metric.target);
    const progress = Number(metric.progress);
    const unit = typeof metric.unit === "string" ? metric.unit.trim() : "";
    if (!title.trim() || !Number.isFinite(target) || target <= 0 || !Number.isFinite(progress) || progress < 0 || !unit) return metrics;
    metrics[title] = { target, unit, progress };
    return metrics;
  }, {});
}

function normalizeDayStartTime(value: unknown): string {
  if (typeof value !== "string") return defaultState.settings.dayStartTime;
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return defaultState.settings.dayStartTime;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return defaultState.settings.dayStartTime;
  }
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function normalizeTimezone(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) return defaultState.settings.timezone;
  const timezone = value.trim();
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format();
    return timezone;
  } catch {
    return defaultState.settings.timezone;
  }
}

export function saveState(state: AppState, allowOptionalStorage = true): void {
  if (!allowOptionalStorage) return;
  const persisted = {
    settings: state.settings,
    weeklyGoalCategories: state.weeklyGoalCategories,
    dismissedSuggestionIds: state.dismissedSuggestionIds,
    schemaVersion: state.schemaVersion,
    updatedAt: state.updatedAt,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
}
